import React, {memo, useMemo} from 'react';
import config from '../../config/GraphViewConfig';
import colors from "../../../../../../../../utils/Colors";
import PlusIcon from "../../../../../../../../assets/icons/PlusIcon";
import "./styles.css"
import {getFileNameFromLink, processNodeButton, stripHtml, truncateTextToFit} from "../../../../util";
import LinkIcon from "../../../../../../../../assets/icons/LinkIcon";
import Translate from "../../../../../../../../components/Translate";
import PowerIcon from "../../../../../../../../assets/icons/PowerIcon";
import PhoneCallIcon from "../../../../../../../../assets/icons/PhoneCallIcon";
import TransferNode from "./TransferNode";
import Attachment from "./Attachment";
import {useGraphRelationContext} from "../../services/ButtonRelationService";
import Point from "../../structures/Point";
import HouseIcon from "../../../../../../../../assets/icons/HouseIcon";
import ForumIcon from "../../../../../../../../assets/icons/ForumIcon";
import graphConfig from "../../../../config";

/*
 * How many of a node's messages the card shows before it starts counting.
 *
 * Three is what fits, and it is also about as much as anyone reads off a card
 * while looking at the shape of a tree - past that the number is the useful part.
 */
const MAX_MESSAGE_ROWS = 3;

/*
 * How many attachment chips fit before the action marks on the other side of the
 * foot. Five rather than the eight it was: past that they were running into the
 * marks, and a row of eight identical little squares says no more than a row of
 * five and a number does.
 */
const MAX_ATTACHMENT_CHIPS = 5;

/* where the foot of the card sits, measured from the node's centre */
const FOOT_Y = 46;

/*
 * A chat bubble: rounded everywhere except the corner nearest the speaker.
 *
 * The notch is the whole reason this is a path and not a rect with an rx. It is
 * what the eye reads as "something said", and it costs one line segment.
 */
const bubblePath = (x, y, width, height) => {
    const r = 10;
    const notch = 3;

    return [
        `M ${x + notch} ${y}`,
        `H ${x + width - r}`,
        `Q ${x + width} ${y} ${x + width} ${y + r}`,
        `V ${y + height - r}`,
        `Q ${x + width} ${y + height} ${x + width - r} ${y + height}`,
        `H ${x + r}`,
        `Q ${x} ${y + height} ${x} ${y + height - r}`,
        `V ${y + notch}`,
        'Z'
    ].join(' ');
};

const Node = ({
                  node,
                  isHighlighted,
                  onSelect,
                  onAddButton,
                  onHighlightButtonEdge,
                  onUnHighlightButtonEdge,
                  onHighlightNodeEdges,
                  onUnHighlightNodeEdges,
                  onCreateNewNode,
                  connectButtonToDestination,
                  onNodeContextMenu,
                  onButtonContextMenu
              }) => {

    const {activeButtonId, setActiveButtonId, setStartPoint, setEndPoint} = useGraphRelationContext()

    const messages = useMemo(() => {
        return node.data.message.split('/n')
    }, [node]);

    /*
     * Another kind of node altogether, and drawn by its own component rather
     * than by a tangle of conditions in this one. It has no buttons, no plus, no
     * action flags, no attachments and no list of messages - which is most of
     * what is below - so sharing this code would mean switching nearly all of it
     * off, and a reader would have to hold both shapes in mind at once.
     *
     * Below the hooks, not above them: a node can change type while it is on
     * screen, and returning before a hook ran would leave React with fewer of
     * them than the render before - which it treats as an error, not a resize.
     */
    if (node.data.nodeType === graphConfig.nodeTypes.operatorTransfer) {
        return (
            <TransferNode
                node={node}
                isHighlighted={isHighlighted}
                onSelect={onSelect}
                onNodeContextMenu={onNodeContextMenu}
                connectButtonToDestination={connectButtonToDestination}
            />
        );
    }

    const nodeConfig = config.design.node;
    const pad = nodeConfig.padding;

    /*
     * The card holds three rows of text and no more, and the third is given up
     * to the count as soon as there is a count to show - so a node with four
     * messages and a node with forty are the same height, and the row that would
     * have been clipped says how many instead.
     */
    const hiddenMessageCount = messages.length > MAX_MESSAGE_ROWS
        ? messages.length - (MAX_MESSAGE_ROWS - 1)
        : 0;
    const visibleMessages = messages.slice(
        0, hiddenMessageCount > 0 ? MAX_MESSAGE_ROWS - 1 : MAX_MESSAGE_ROWS
    );

    const buttons = (node && node.data && Array.isArray(node.data.buttons)) ? node.data.buttons.map(processNodeButton) : [];

    const top = -node.height / 2;

    const portCx = 0;
    const portCy = top + nodeConfig.port.height / 2;

    const bodyTop = top + nodeConfig.port.height + nodeConfig.nodeAndNodePortGap;
    const bodyX = -nodeConfig.width / 2;
    const bodyY = bodyTop;

    const btnY = bodyTop + nodeConfig.height + nodeConfig.nodeAndButtonContainerGap;

    const totalButtonsWidth = buttons.length > 0
        ? (buttons.length + 1) * nodeConfig.button.width + (buttons.length) * nodeConfig.buttonsGap
        : nodeConfig.button.width;
    const startBtnX = -totalButtonsWidth / 2;

    return (
        <g
            className={"tree-node-container " + (node.isFree ? 'free' : '')}
            data-node-id={node.id}
            transform={`translate(${node.x + node.width / 2},${node.y + node.height / 2})`}
        >
            {/*
              * Where the edges land. A circle rather than the diamond it was:
              * in a flow drawing a diamond is a decision, and this is an inlet -
              * the decisions on this canvas are the buttons.
              */}
            <circle
                id={`node-port-${node.id}`}
                cx={portCx}
                cy={portCy}
                r={nodeConfig.port.width / 2}
                className={'tree-node-port' + (isHighlighted ? ' highlighted' : '')}
            />

            <g
                onClick={() => onSelect([])}
                onContextMenu={e => onNodeContextMenu(e, node)}
                className="tree-node-body"
                onMouseUp={(event) => {

                    if (!activeButtonId /*|| node.data.buttons.some(btn => btn.id === activeButtonId)*/) return

                    event.stopPropagation();

                    connectButtonToDestination(activeButtonId, node.id)

                    setActiveButtonId(null);
                    setStartPoint(null);
                    setEndPoint(null);
                }}
            >
                <rect
                    x={bodyX}
                    y={bodyY}
                    width={nodeConfig.width}
                    height={nodeConfig.height}
                    rx={18}
                    ry={18}
                    strokeWidth={1}
                    filter="url(#nodeShadow)"
                    className={'tree-node-body__rect' + (node.isRoot ? ' is-root' : '')}
                    onMouseOver={() => onHighlightNodeEdges(node.id)}
                    onMouseOut={() => onUnHighlightNodeEdges(node.id)}
                />

                {/*
                  * The title, in text rather than reversed out of a navy band.
                  *
                  * Every node used to wear a 36px block of the darkest colour in
                  * the palette, so a tree of any size was read as a field of
                  * navy stripes with the content squeezed underneath. The colour
                  * now belongs to the dot and to what the bot says; the name is
                  * just a name, and reads like one.
                  */}
                <circle
                    cx={bodyX + pad + 5}
                    cy={bodyY + 21}
                    r={5}
                    className={'tree-node-mark' + (node.isRoot ? ' is-root' : '')}
                />

                <text
                    x={bodyX + pad + 18}
                    y={bodyY + 26}
                    textAnchor="start"
                    fontSize={13}
                    fontWeight={600}
                    pointerEvents="none"
                    className="tree-node-title"
                >
                    {truncateTextToFit((node.data.name ? node.data.name : node.id), nodeConfig.width - 48, 13)}
                </text>

                {/* a hairline instead of the band's hard edge */}
                <line
                    x1={bodyX + pad}
                    y1={bodyY + 40}
                    x2={bodyX + nodeConfig.width - pad}
                    y2={bodyY + 40}
                    className="tree-node-rule"
                />

                {/*
                  * What the bot says, drawn the way the bot says it.
                  *
                  * These were white cards on a white card, each with its own
                  * border and its own shadow - three shadows stacked inside a
                  * fourth, and nothing to show for it. As tinted bubbles they
                  * need neither, and the canvas starts to look like the
                  * conversation it configures rather than like a flowchart that
                  * happens to be about one.
                  */}
                {visibleMessages.map((msg, idx) => {
                    const msgY = bodyY + 50 + idx * 30;
                    return (
                        <g key={idx} transform={`translate(0, ${msgY})`} onClick={(event) => {
                            event.stopPropagation();
                            onSelect(['messages'])
                        }}>
                            <path
                                d={bubblePath(bodyX + pad, 0, nodeConfig.width - pad * 2, 24)}
                                className="tree-node-bubble"
                            />
                            <text
                                x={bodyX + pad + 10}
                                y={16}
                                textAnchor="start"
                                fontSize={12}
                                pointerEvents="none"
                                className="tree-node-bubble__text"
                            >
                                {truncateTextToFit(stripHtml(msg).trim(), nodeConfig.width - 46, 12)}
                            </text>
                        </g>
                    );
                })}

                {hiddenMessageCount > 0 && (
                    <text
                        x={bodyX + pad + 10}
                        y={bodyY + 50 + visibleMessages.length * 30 + 16}
                        textAnchor="start"
                        fontSize={11}
                        fontWeight={600}
                        pointerEvents="none"
                        className="tree-node-more"
                    >
                        +{hiddenMessageCount} <Translate text="more"/>
                    </text>
                )}

                {node.data.botAttachments && node.data.botAttachments.length &&
                    <g
                        className="tree-node-attachments-container"
                        transform={`translate(${-(nodeConfig.width / 2) + 22}, ${FOOT_Y})`}
                        onClick={(event) => {
                            event.stopPropagation()
                            onSelect(['attachments'])
                        }}
                    >
                        {
                            node.data.botAttachments.slice(0, MAX_ATTACHMENT_CHIPS).map((attachment, idx) => {
                                return (
                                    <g
                                        key={attachment.link}
                                        transform={`translate(${idx * 28}, 0)`}
                                    >
                                        <rect
                                            x={-10}
                                            y={0}
                                            width={22}
                                            height={22}
                                            rx={6}
                                            ry={6}
                                            className="tree-node-attachment__rect"
                                            strokeWidth={1}
                                        />
                                        <g transform="translate(-7, 3)">
                                            <Attachment
                                                link={attachment.link}
                                                fileName={getFileNameFromLink(attachment.link)}
                                            />
                                        </g>
                                    </g>
                                )
                            })
                        }

                        {node.data.botAttachments.length > MAX_ATTACHMENT_CHIPS &&
                            <text
                                x={(MAX_ATTACHMENT_CHIPS * 28) - 10}
                                y={14}
                                textAnchor="left"
                                fontSize={12}
                                fontWeight={600}
                                style={{color: colors.silverBlue}} fill="currentColor"
                                pointerEvents="none"
                            >
                                (+{node.data.botAttachments.length - MAX_ATTACHMENT_CHIPS})
                            </text>
                        }
                    </g>
                }
                {/*
                  * The foot of the card: what it carries on the left, what it
                  * does on the right, on one line. They used to be at different
                  * heights and each sized for a card that no longer exists, so
                  * eight attachment chips ran straight through the action marks.
                  */}
                <g className="tree-node-actions-container"
                   transform={`translate(${(nodeConfig.width / 2) - 46}, ${FOOT_Y + 1})`}>

                    <g transform="translate(-30,0)">
                        <rect
                            x={-10}
                            y={0}
                            width={18}
                            height={18}
                            rx={4}
                            ry={4}
                            stroke="#fff"
                            fill="#fff"
                            strokeWidth={1}
                        />
                        <g transform="translate(-7, 3)">
                            <PowerIcon height={12} width={12}
                                       style={{color: node.data.terminalMessage ? colors.red : colors.silverDark}}
                                       stroke="currentColor"/>
                        </g>
                    </g>

                    <g transform="translate(-10,0)">
                        <rect
                            x={-10}
                            y={0}
                            width={18}
                            height={18}
                            rx={4}
                            ry={4}
                            stroke="#fff"
                            fill="#fff"
                            strokeWidth={1}
                        />
                        <g transform="translate(-7, 3)">
                            <HouseIcon height={12} width={12}
                                       style={{color: node.data.toStart ? colors.green : colors.silverDark}}
                                       stroke="currentColor"/>
                        </g>
                    </g>

                    <g transform="translate(10,0)">
                        <rect
                            x={-10}
                            y={0}
                            width={18}
                            height={18}
                            rx={4}
                            ry={4}
                            stroke="#fff"
                            fill="#fff"
                            strokeWidth={1}
                        />
                        <g transform="translate(-7, 3)">
                            <ForumIcon height={12} width={12}
                                       style={{color: node.data.chatWithOperator ? colors.blueDark : colors.silverDark}}/>
                        </g>
                    </g>

                    <g transform="translate(30,0)">
                        <rect
                            x={-10}
                            y={0}
                            width={18}
                            height={18}
                            rx={4}
                            ry={4}
                            stroke="#fff"
                            fill="#fff"
                            strokeWidth={1}
                        />
                        <g transform="translate(-7, 3)">
                            <PhoneCallIcon height={12} width={12}
                                           style={{color: node.data.callToOperator ? colors.yellow : colors.silverDark}}
                                           stroke="currentColor"/>
                        </g>
                    </g>

                </g>

            </g>

            <g className="tree-node-buttons-container">
                {buttons.map((btn, i) => {
                    const btnX = startBtnX + i * (nodeConfig.button.width + nodeConfig.buttonsGap);
                    return (
                        <g
                            key={i}
                            id={`node-button-${btn.id}`}
                            className="tree-node-button"
                            transform={`translate(${btnX},${btnY})`}
                            onMouseOver={() => onHighlightButtonEdge(btn.id)}
                            onMouseLeave={() => onUnHighlightButtonEdge(btn.id)}
                            onClick={(event) => {
                                event.stopPropagation();
                                onCreateNewNode(btn.id)
                            }}
                            onContextMenu={e => {
                                onButtonContextMenu(e, node, btn)
                            }}
                            onMouseDown={(event) => {
                                event.stopPropagation();
                                setActiveButtonId(btn.id);
                                const point = new Point((node.x + node.width / 2) + btnX + (nodeConfig.button.width / 2), (node.y + node.height / 2) + btnY + nodeConfig.button.height)
                                setStartPoint(point)
                                setEndPoint(point)
                            }}
                            onMouseUp={(event) => {
                                setActiveButtonId(null)
                                setStartPoint(null)
                                event.stopPropagation()
                            }}
                        >
                            <title>{btn.shortText}</title>

                            {/*
                              * A hit area that does not move.
                              *
                              * In SVG the pointer is caught by what is painted,
                              * so a pill that lifts on hover lifts out from under
                              * the pointer that is hovering it - which ends the
                              * hover, which drops it back, which starts the hover
                              * again. This stays put and is a little larger than
                              * the pill, so the pointer never leaves it.
                              */}
                            <rect
                                x={-4}
                                y={-4}
                                width={nodeConfig.button.width + 8}
                                height={nodeConfig.button.height + 8}
                                fill="transparent"
                            />

                            <g className="tree-node-button__lift">
                            {/* a pill, outlined, exactly as a quick reply is
                                drawn in the chat itself - these are the same
                                buttons, so they may as well look like them */}
                            <rect
                                x={0}
                                y={0}
                                width={nodeConfig.button.width}
                                height={nodeConfig.button.height}
                                rx={nodeConfig.button.height / 2}
                                ry={nodeConfig.button.height / 2}
                                className="tree-node-button-body-rect"
                                strokeWidth={1.5}
                                filter="url(#softShadow)"
                            />
                            <text
                                x={nodeConfig.button.width / 2}
                                y={nodeConfig.button.height / 2 + 4}
                                textAnchor="middle"
                                fontSize={12}
                                fontWeight={'bold'}
                                pointerEvents="none"
                            >
                                {truncateTextToFit(btn.shortText, nodeConfig.button.width, 12)}
                            </text>

                            {btn.hasLink && (
                                <g
                                    transform={`translate(${nodeConfig.button.width - 8}, -6)`}
                                >
                                    <rect
                                        x={-10}
                                        y={0}
                                        width={16}
                                        height={16}
                                        rx={4}
                                        ry={4}
                                        fill="#fff"
                                        stroke="#fff"
                                        strokeWidth={1}
                                        filter="url(#softShadow)"
                                    />
                                    <g transform="translate(-7, 3)">
                                        <LinkIcon height={10} width={10} style={{color: colors.blueDark}}
                                                  stroke="currentColor"/>
                                    </g>
                                </g>
                            )}
                            </g>
                        </g>
                    );
                })}

                <g
                    className="add-tree-node-button"
                    transform={`translate(${startBtnX + buttons.length * (nodeConfig.button.width + nodeConfig.buttonsGap)}, ${btnY})`}
                >
                    {/* the stationary hit area, as on the pills beside it */}
                    <rect
                        x={-4}
                        y={-4}
                        width={nodeConfig.button.width + 8}
                        height={nodeConfig.button.height + 8}
                        fill="transparent"
                        style={{cursor: 'pointer'}}
                        onClick={onAddButton}
                    />

                    <g className="add-tree-node-button__lift">
                    {/* dashed, because it is an invitation rather than a
                        thing - the solid pills beside it all lead somewhere */}
                    <rect
                        x={0}
                        y={0}
                        width={nodeConfig.button.width}
                        height={nodeConfig.button.height}
                        rx={nodeConfig.button.height / 2}
                        ry={nodeConfig.button.height / 2}
                        strokeWidth={1.5}
                        strokeDasharray="5 4"
                        className="add-tree-node-button__rect"
                        style={{cursor: 'pointer'}}
                        onClick={onAddButton}
                    />

                    <g transform={`translate(${(nodeConfig.button.width - 18) / 2}, ${(nodeConfig.button.height - 18) / 2})`}>
                        <PlusIcon width={18} height={18} className="add-tree-node-button__icon" stroke="currentColor"/>
                    </g>
                    </g>
                </g>
            </g>
        </g>
    );

};

export default memo(Node);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/GraphView/components/Node/index.jsx